import React, { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import WidgetShell from './WidgetShell';
import { getCurrentResults } from './strategyDataCache';
import { transformSheetToResultsDashboardModel } from '../../utils/transformResults';
import StrategyRowCard from '../StrategyRowCard';
import { IconEye, IconEyeOff, IconGrip } from '../Icons';
import { getResultsNavLabel } from '../../utils/activeEntity';

/**
 * Sortable wrapper around one StrategyRowCard, used while the dashboard is in
 * customize mode. The grip button on the left starts a drag inside the widget;
 * the eye button on the right hides the strategy.
 */
function SortableStrategyRow({ id, strategy, category, contractType, contractSize, onHide }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 5 : undefined,
    opacity: isDragging ? 0.7 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`dashboard-current-results-row is-editing${isDragging ? ' is-dragging' : ''}`}
    >
      <button
        type="button"
        className="dashboard-current-results-grip"
        aria-label={`Reorder ${strategy.strategyName}`}
        title="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <IconGrip size={14} />
      </button>
      <div className="dashboard-current-results-row-card">
        <StrategyRowCard
          strategy={strategy}
          category={category}
          contractType={contractType}
          contractSize={contractSize}
        />
      </div>
      <button
        type="button"
        className="dashboard-current-results-hide"
        aria-label={`Hide ${strategy.strategyName}`}
        title="Hide strategy"
        onClick={onHide}
      >
        <IconEyeOff size={14} />
      </button>
    </div>
  );
}

function CurrentResultsTableWidget({ config, user, editing, onConfigChange }) {
  const [rawRows, setRawRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCurrentResults({ email: user?.email })
      .then((data) => {
        if (cancelled) return;
        setRawRows(Array.isArray(data?.rows) ? data.rows : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load Current Results');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.email, reloadToken]);

  const subtitle = useMemo(
    () => (config?.note ? String(config.note).slice(0, 60) : `${getResultsNavLabel()} · Current Results sheet`),
    [config?.note],
  );

  const model = useMemo(
    () => transformSheetToResultsDashboardModel(rawRows || []),
    [rawRows],
  );

  const contractType = config?.contractType || 'NQ';
  const contractSize = Number(config?.contractSize) || 1;

  /**
   * Resolve the visible + hidden strategy lists from `model` against the user's
   * stored config (hiddenStrategies + strategyOrder). Strategies new to the source
   * (not in either list) are appended to visible in their natural order.
   */
  const { visible, hidden } = useMemo(() => {
    const all = model.groups.flatMap((g) => g.strategies.map((s) => ({ strategy: s, category: g.name })));
    const byName = new Map(all.map((entry) => [entry.strategy.strategyName, entry]));
    const hiddenSet = new Set(config?.hiddenStrategies || []);
    const order = Array.isArray(config?.strategyOrder) ? config.strategyOrder : [];

    const visibleOut = [];
    const seen = new Set();
    order.forEach((name) => {
      if (hiddenSet.has(name) || seen.has(name)) return;
      const entry = byName.get(name);
      if (entry) {
        visibleOut.push(entry);
        seen.add(name);
      }
    });
    all.forEach((entry) => {
      if (hiddenSet.has(entry.strategy.strategyName) || seen.has(entry.strategy.strategyName)) return;
      visibleOut.push(entry);
      seen.add(entry.strategy.strategyName);
    });

    const hiddenOut = all.filter((entry) => hiddenSet.has(entry.strategy.strategyName));
    return { visible: visibleOut, hidden: hiddenOut };
  }, [model, config?.hiddenStrategies, config?.strategyOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (typeof onConfigChange !== 'function') return;
    const visibleNames = visible.map((entry) => entry.strategy.strategyName);
    const oldIdx = visibleNames.indexOf(active.id);
    const newIdx = visibleNames.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reorderedVisible = arrayMove(visibleNames, oldIdx, newIdx);
    // Persist order as visible-only-but-stable: visible reordered names + existing hidden names
    // appended in their original order, so unhiding later restores them at the bottom.
    const hiddenNames = hidden.map((entry) => entry.strategy.strategyName);
    onConfigChange({ strategyOrder: [...reorderedVisible, ...hiddenNames] });
  };

  const setHidden = (name, hide) => {
    if (typeof onConfigChange !== 'function') return;
    const current = new Set(config?.hiddenStrategies || []);
    if (hide) current.add(name);
    else current.delete(name);
    onConfigChange({ hiddenStrategies: Array.from(current) });
  };

  const visibleIds = visible.map((entry) => entry.strategy.strategyName);

  return (
    <WidgetShell
      title="Current Results"
      subtitle={subtitle}
      loading={loading}
      error={error}
      onRetry={() => setReloadToken((t) => t + 1)}
      bodyClassName="dashboard-widget-body-current-results"
    >
      {!rawRows || (visible.length === 0 && hidden.length === 0) ? (
        <div className="dashboard-widget-state">No Current Results data available.</div>
      ) : (
        <div className="dashboard-current-results-list">
          {editing ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
                {visible.map(({ strategy, category }) => (
                  <SortableStrategyRow
                    key={strategy.strategyName}
                    id={strategy.strategyName}
                    strategy={strategy}
                    category={category}
                    contractType={contractType}
                    contractSize={contractSize}
                    onHide={() => setHidden(strategy.strategyName, true)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            visible.map(({ strategy, category }) => (
              <div key={strategy.strategyName} className="dashboard-current-results-row">
                <div className="dashboard-current-results-row-card">
                  <StrategyRowCard
                    strategy={strategy}
                    category={category}
                    contractType={contractType}
                    contractSize={contractSize}
                  />
                </div>
              </div>
            ))
          )}

          {editing && hidden.length > 0 ? (
            <div className="dashboard-current-results-hidden">
              <div className="dashboard-current-results-hidden-title">
                Hidden ({hidden.length})
              </div>
              <ul className="dashboard-current-results-hidden-list">
                {hidden.map(({ strategy, category }) => (
                  <li key={strategy.strategyName} className="dashboard-current-results-hidden-item">
                    <span className="dashboard-current-results-hidden-name">
                      {strategy.strategyName}
                      {category ? <em>{category}</em> : null}
                    </span>
                    <button
                      type="button"
                      className="dashboard-current-results-hidden-show"
                      onClick={() => setHidden(strategy.strategyName, false)}
                      title="Show strategy"
                      aria-label={`Show ${strategy.strategyName}`}
                    >
                      <IconEye size={14} /> <span>Show</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </WidgetShell>
  );
}

export default CurrentResultsTableWidget;
